import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { authErrorMessage, type AuthErrorCode } from '@platform/schema';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';

import { PageHeader, SCREEN_GAP } from './PageHeader';

interface FromState {
  from?: { pathname?: string };
}

export function SignIn() {
  const { signIn, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<AuthErrorCode | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const destination =
    (location.state as FromState | null)?.from?.pathname ?? '/';

  // Someone who is already signed in has no business on this screen — arriving here
  // by typing the URL should not require signing in again.
  if (!loading && isAuthenticated) {
    return <Navigate to={destination} replace />;
  }

  const submit = () => {
    setSubmitting(true);
    setError(null);

    void (async () => {
      const failure = await signIn(email, password);
      setError(failure);
      setSubmitting(false);
      // No navigate on success: the auth state change re-renders this screen, and the
      // redirect above takes it from there. One path in, rather than two.
    })();
  };

  return (
    <Box
      sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}
    >
      <Paper
        variant="outlined"
        sx={{ p: SCREEN_GAP, width: '100%', maxWidth: 400 }}
      >
        <Box sx={{ mb: SCREEN_GAP }}>
          <PageHeader
            title="Sign in"
            subtitle="Back-office access. Accounts are created by an operator."
          />
        </Box>

        {/* The event type is inferred rather than named: React's `FormEvent` alias
            is deprecated, and naming it is the only reason this needed a type. */}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          noValidate
        >
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              autoComplete="username"
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              autoComplete="current-password"
              required
              fullWidth
            />

            {/* The typed message, never the provider's string. It deliberately does
                not say whether the address has an account — a form that tells them
                apart is a way to find out who has one. */}
            {error !== null && (
              <Alert severity="error">{authErrorMessage(error)}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              fullWidth
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
